import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export interface TaggableUser {
  id: string;
  groupName: string;
  name: string;
}

export type TaggableUserOption = TaggableUser & { display: string };

/** The resource whose participants can be tagged. */
export type TaggableUserResource = {
  resourceType: 'LocationEpisode' | 'Review' | 'WorkflowAction' | 'ChatConversation';
  resourceId: string;
};

/** Tag on the participants of a review. Returns undefined when there is no
 * review yet, which leaves the caller's default in place. */
export const taggableUsersOnReview = (reviewId: string | undefined): TaggableUserResource | undefined =>
  reviewId ? { resourceType: 'Review', resourceId: reviewId } : undefined;

/** Tag on the participants of a location episode. */
export const taggableUsersOnLocationEpisode = (
  locationEpisodeId: string | undefined
): TaggableUserResource | undefined =>
  locationEpisodeId ? { resourceType: 'LocationEpisode', resourceId: locationEpisodeId } : undefined;

/** Tag on the participants of one workflow action — the building it is scoped to, or every
 * building the run targets when it is ungrouped. */
export const taggableUsersOnWorkflowAction = (actionId: string | undefined): TaggableUserResource | undefined =>
  actionId ? { resourceType: 'WorkflowAction', resourceId: actionId } : undefined;

/** Tag on the members of a chat conversation — exactly the users who can see that channel
 * (the roster is exact-by-construction server-side). */
export const taggableUsersOnChatConversation = (
  conversationId: string | undefined
): TaggableUserResource | undefined =>
  conversationId ? { resourceType: 'ChatConversation', resourceId: conversationId } : undefined;

type IndexParams = TaggableUserResource & BaseIndexQueryParams;

export async function indexTaggableUsers(params: IndexParams, signal: GenericAbortSignal) {
  return http.get<Paginated<TaggableUser>>('taggable_users', { params, signal }).then((res) => ({
    ...res.data,
    data: res.data.data.map<TaggableUserOption>((user) => ({
      ...user,
      display: `${user.name} - ${user.groupName}`,
    })),
  }));
}
